import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { invitation } from '../data/invitation.js'
import { useCountdown } from '../hooks/useCountdown.js'
import { useScrollScrub } from '../hooks/useScrollScrub.js'
import { ramp } from '../lib/math.js'
import { prefersReducedMotion } from '../lib/motion.js'
import { shouldSeek, videoSeekTarget } from '../lib/scrub.js'
import styles from './EndingFilm.module.css'

export interface EndingFilmProps {
  src: string | undefined
  poster?: string
  /**
   * blob이 걸려 있을 때만 'auto'를 준다. 스트리밍 소스에 'auto'를 걸면
   * 백그라운드 워밍이 받고 있는 바로 그 파일을 동시에 한 번 더 받는다 —
   * 하객 한 사람당 8MB짜리 중복이다.
   */
  preload?: 'auto' | 'metadata' | 'none'
}

/** 디자인 원본의 `_wheelCell` — 숫자 하나를 담는 셀 DOM을 만든다. */
function buildCell(value: string): HTMLDivElement {
  const cell = document.createElement('div')
  cell.className = styles.cell!
  cell.textContent = value
  return cell
}

interface FlipDigitProps {
  value: string
  label: string
}

/**
 * 카운트다운 숫자 하나. 값이 바뀌면 새 숫자를 위에, 옛 숫자를 아래에 쌓고
 * `translateY(-76px)`에서 `0`으로 원본과 같은 지속시간·이징으로 굴린다.
 * 디자인 원본처럼 strip 안쪽은 React가 아니라 이 effect가 전적으로 관리한다 —
 * 리액트가 그린 자식을 imperative하게 바꿔치기하면 재조정과 충돌한다.
 */
function FlipDigit({ value, label }: FlipDigitProps) {
  const stripRef = useRef<HTMLDivElement>(null)
  const prevValue = useRef<string | null>(null)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useLayoutEffect(() => {
    const strip = stripRef.current
    if (strip === null) return

    if (prevValue.current === null) {
      // 최초 마운트 — 애니메이션 없이 채운다.
      strip.replaceChildren(buildCell(value))
      prevValue.current = value
      return
    }

    if (prevValue.current === value) return

    // 모션 줄이기를 켠 하객에게는 굴리지 않고 갈아 끼운다.
    if (prefersReducedMotion()) {
      clearTimeout(resetTimer.current)
      strip.replaceChildren(buildCell(value))
      strip.style.transition = 'none'
      strip.style.transform = 'translateY(0)'
      prevValue.current = value
      return
    }

    const old = prevValue.current
    clearTimeout(resetTimer.current)
    strip.replaceChildren(buildCell(value), buildCell(old))
    strip.style.transition = 'none'
    strip.style.transform = 'translateY(-76px)'
    // 강제 리플로우로 위 상태를 확정한 뒤에야 트랜지션이 걸린다.
    void strip.offsetHeight
    strip.style.transition = 'transform .5s cubic-bezier(.25,.8,.3,1)'
    strip.style.transform = 'translateY(0)'
    resetTimer.current = setTimeout(() => {
      strip.style.transition = 'none'
      strip.replaceChildren(buildCell(value))
      strip.style.transform = 'translateY(0)'
    }, 560)

    prevValue.current = value
  }, [value])

  useEffect(() => () => clearTimeout(resetTimer.current), [])

  return (
    <div className={styles.digitCol}>
      <div className={styles.flipBox}>
        <div ref={stripRef} className={styles.strip} />
      </div>
      <div className={styles.flipLabel}>{label}</div>
    </div>
  )
}

/** 원본 data-screen-label="10 카운트다운" 블록. */
export function EndingFilm({ src, poster, preload = 'metadata' }: EndingFilmProps) {
  const wrap = useRef<HTMLDivElement>(null)
  const video = useRef<HTMLVideoElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const guide = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useScrollScrub(wrap, (p) => {
    // currentTime은 끝에서 SEEK_TAIL만큼 남으므로 스크럽 진행률로 100%를 판단한다.
    // 같은 상태의 속성을 매 프레임 다시 쓰지 않아 페이드/모션이 재시작되지 않는다.
    // 브라우저가 scrollY를 픽셀로 반올림해 p=0.9998 등에 멈춰도 표시 진행률과 맞춘다.
    const percent = Math.round(p * 100)
    const guideActive = String(src !== undefined && percent > 0 && percent < 100)
    if (guide.current !== null && guide.current.dataset.active !== guideActive) {
      guide.current.dataset.active = guideActive
    }
    const v = video.current
    if (v !== null && v.readyState >= 1) {
      const target = videoSeekTarget(p, v.duration)
      if (shouldSeek(v.currentTime, target)) {
        try {
          v.currentTime = target
        } catch {
          /* 시킹 불가 상태 */
        }
      }
    }
    // 디자인 원본의 구간 계수를 그대로 쓴다.
    if (box.current !== null) {
      const i = ramp(p, 0.1, 0.3)
      box.current.style.opacity = String(i)
      box.current.style.transform = `translateY(${((1 - i) * 28).toFixed(1)}px)`
    }
  })

  // 하객이 인사말·본문을 읽는 화면 위쪽에 있는 동안엔 1초마다 리렌더할 이유가
  // 없다 — 이 섹션이 화면에 걸려 있을 때만 카운트다운을 돌린다.
  useEffect(() => {
    const section = wrap.current
    if (section === null) return
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry !== undefined) {
        setVisible(entry.isIntersecting)
        // 빠르게 다른 섹션으로 건너뛰면 스크럽의 마지막 프레임 없이 컬링될 수 있다.
        if (!entry.isIntersecting && guide.current !== null) guide.current.dataset.active = 'false'
      }
    })
    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  const parts = useCountdown(invitation.weddingAt, visible)

  // HeroFilm과 같은 iOS 초기화지만, 두 영상을 동시에 play하지 않도록 엔딩이
  // 화면에 들어온 뒤에만 수행한다. 그전이나 재생이 차단되는 환경에서는 poster가
  // 빈 영상 영역을 대신하고, 허용되면 첫 프레임을 디코딩한 뒤 스크럽 전용으로 멈춘다.
  useEffect(() => {
    const v = video.current
    if (v === null || src === undefined || !visible) return
    let active = true
    v.muted = true
    void v.play()
      .then(() => {
        if (active) v.pause()
      })
      .catch(() => {
        // 저전력 모드·브라우저 정책이 재생을 거절해도 poster로 안전하게 물러난다.
      })
    return () => {
      active = false
      v.pause()
    }
  }, [src, visible])

  return (
    <div ref={wrap} className={styles.wrap}>
      <div className={styles.sticky}>
        <video
          ref={video}
          className={styles.video}
          muted
          playsInline
          preload={preload}
          {...(poster ? { poster } : {})}
          {...(src ? { src } : {})}
        />
        <div className={styles.toneWash} />
        <div className={styles.blur} />
        <div className={styles.badge}>ENDING FILM</div>
        <div className={styles.contentLayer}>
          <div className={styles.radial} />
          <div ref={box} className={styles.box}>
            <div className={styles.caption}>counting down</div>
            <div className={styles.subCaption}>결혼식까지 남은 시간</div>
            <div className={styles.digits}>
              <FlipDigit value={parts.days} label="DAYS" />
              <FlipDigit value={parts.hours} label="HOURS" />
              <FlipDigit value={parts.minutes} label="MIN" />
              <FlipDigit value={parts.seconds} label="SEC" />
            </div>
          </div>
        </div>
        <div
          ref={guide}
          className={styles.scrollGuide}
          data-testid="ending-scroll-guide"
          data-active="false"
          aria-hidden="true"
        >
          <svg className={styles.guideHand} viewBox="0 0 36 46" focusable="false">
            <path d="M8.5 30V7.5a3.5 3.5 0 0 1 7 0v18.3" />
            <path d="M15.5 23v-4.5a3.25 3.25 0 0 1 6.5 0v7.6" />
            <path d="M22 24v-3a3.25 3.25 0 0 1 6.5 0v6.2" />
            <path d="M28.5 25a3.25 3.25 0 0 1 3.25 3.25v1.25c0 7.5-6.1 13.6-13.6 13.6h-.6c-4.2 0-8.2-1.9-10.8-5.2l-3.6-4.6a3.25 3.25 0 0 1 .55-4.55 3.25 3.25 0 0 1 4.5.5L8.5 30" />
          </svg>
        </div>
      </div>
    </div>
  )
}
